import axios from 'axios';
import waveAuth from './waveAuth';
import { Toast } from 'monday-ui-react-core';

const WAVE_API_BASE_URL = 'https://gql.waveapps.com/graphql/public';

class WaveApiService {
  constructor() {
    // Initialize with client credentials
    this.clientId = process.env.REACT_APP_WAVE_CLIENT_ID;
    this.clientSecret = process.env.REACT_APP_WAVE_CLIENT_SECRET;
    
    this.client = axios.create({
      baseURL: WAVE_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Initialize auth on service creation
    this.initializeAuth();
  }

  // New method to initialize authentication
  async initializeAuth() {
    try {
      // Check if we have stored tokens
      if (!waveAuth.isAuthenticated()) {
        console.log('No Wave authentication found, initiating auth flow...');
        waveAuth.initiateAuth();
        return;
      }

      // Set up interceptors after we have authentication
      this.setupInterceptors();
    } catch (error) {
      console.error('Wave API initialization error:', error);
      Toast.show({
        message: 'Failed to initialize Wave connection',
        type: Toast.types.ERROR
      });
    }
  }

  // Set up axios interceptors
  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(async (config) => {
      if (!waveAuth.isAuthenticated()) {
        throw new Error('Not authenticated with Wave');
      }

      config.headers.Authorization = `Bearer ${waveAuth.getAccessToken()}`;
      return config;
    });

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await waveAuth.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${waveAuth.getAccessToken()}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            waveAuth.logout();
            Toast.show({
              message: 'Session expired. Please log in again.',
              type: Toast.types.ERROR
            });
            window.location.href = '/wave-auth';
            throw refreshError;
          }
        }

        throw error;
      }
    );
  }

  // GraphQL query definitions
  queries = {
    getInvoices: `
      query {
        businesses {
          edges {
            node {
              invoices {
                edges {
                  node {
                    id
                    invoiceNumber
                    status
                    amount
                    customer {
                      name
                    }
                    dueDate
                  }
                }
              }
            }
          }
        }
      }
    `,
    // Add other queries here
  };

  mutations = {
    createInvoice: `
      mutation CreateInvoice($input: InvoiceCreateInput!) {
        invoiceCreate(input: $input) {
          invoice {
            id
            invoiceNumber
          }
        }
      }
    `,
    // Add other mutations here
  };

  // API methods
  async getInvoices() {
    try {
      const response = await this.client.post('', { 
        query: this.queries.getInvoices 
      });
      return response.data;
    } catch (error) {
      await this.handleApiError(error);
      throw error;
    }
  }

  async createInvoice(invoiceData) {
    try {
      const response = await this.client.post('', {
        query: this.mutations.createInvoice,
        variables: { input: invoiceData }
      });
      return response.data;
    } catch (error) {
      await this.handleApiError(error);
      throw error;
    }
  }

  async handleApiError(error) {
    if (error.response?.status === 401) {
      Toast.show({
        message: 'Session expired. Please log in again.',
        type: Toast.types.ERROR
      });
      window.location.href = '/wave-auth';
      return;
    }

    if (error.response?.status === 429) {
      Toast.show({
        message: 'Too many requests. Please try again later.',
        type: Toast.types.ERROR
      });
      return;
    }

    Toast.show({
      message: 'An error occurred. Please try again.',
      type: Toast.types.ERROR
    });
  }
}

// Create and export a singleton instance
const waveApiService = new WaveApiService();
export default waveApiService; 